import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, map, Observable, tap } from "rxjs";
import { ActionResult, ActionResultList, RequestFilter, UserDetail, Request, PagedFilter, ValidatorResult } from "@model";
import { LoaderService } from "./loader.service";
import { EnvironmentService } from "./environment.service";

@Injectable({ providedIn: 'root' })
export class RequestService {
  public static EXISTING_ORGANISER_REQUEST_ON_HOLD = 1;

  private static _fetchNewRequests = new BehaviorSubject<boolean | null>(null);
  private static _currentRequest = new BehaviorSubject<Request | undefined>(undefined);

  get fetchNewRequests() { return RequestService._fetchNewRequests; }
  get currentRequest() { return RequestService._currentRequest; }

  constructor(
    private http: HttpClient,
    private loaderService: LoaderService,
    private environmentService: EnvironmentService
  ) {}

  private handleLoading<T>(observable: Observable<T>, hideLoading?: boolean): Observable<T> {
    if (!hideLoading) this.loaderService.startLoading();
    return observable.pipe(tap(() => !hideLoading && this.loaderService.dismissLoading()));
  }

  sendPublishEventRequest(request: Request, onSuccess: () => void, onError: () => void): Observable<ValidatorResult | null> {
    return this.handleLoading(
      this.http.post<ActionResult<ValidatorResult>>(this.environmentService.getApiUrl() + "/api/requests/event/new", request)
    ).pipe(map(value => {
      const result = value?.result ? ValidatorResult.deserialize(value.result) : null;
      if (result?.hasErrors()) return result;
      result ? onSuccess() : onError();
      return null;
    }));
  }

  manageEventRequest(request: Request): Observable<ValidatorResult | undefined> {
    return this.handleLoading(
      this.http.post<ActionResult<ValidatorResult>>(this.environmentService.getApiUrl() + "/api/requests/manage/event", request)
    ).pipe(map(value => value?.result ? ValidatorResult.deserialize(value.result) : undefined));
  }

  sendOrganiserRequest(request: Request, onSuccess: () => void, onError: (errorCode?: number) => void) {
    this.handleLoading(
      this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + "/api/requests/organiser/new", request)
    ).subscribe(actionResult => (actionResult.resultCode !== -1 && actionResult.resultCode !== 1) ? onSuccess() : onError(actionResult.resultCode));
  }

  manageRequest(url: string, request: Request): Observable<boolean> {
    return this.handleLoading(
      this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + url, request)
    ).pipe(map(value => value.result ?? false));
  }

  manageOrganiserRequest(request: Request): Observable<boolean> {
    return this.manageRequest("/api/requests/manage/organiser", request);
  }

  manageAddEventToLeagueRequest(request: Request): Observable<boolean> {
    return this.manageRequest("/api/requests/manage/league", request);
  }

  filterRequests(requestFilter: PagedFilter<RequestFilter>): Observable<Request[]> {
    return this.http.post<ActionResultList<Request[], number>>(this.environmentService.getApiUrl() + "/api/requests/filter", requestFilter).pipe(
      map(value => value?.result?.length ? value.result : [])
    );
  }

  fetchRequestsCount(requestFilter: RequestFilter): Observable<number> {
    return this.http.post<ActionResult<number>>(this.environmentService.getApiUrl() + "/api/requests/count", requestFilter).pipe(
      map(value => value.result ?? 0)
    )
  }

  fetchRequestById(requestId: number | undefined, hideLoading?: boolean): Observable<Request | null> {
    return this.handleLoading(
      this.http.post<ActionResult<Request>>(this.environmentService.getApiUrl() + "/api/requests/fetchbyid", requestId),
      hideLoading
    ).pipe(map(value => {
      const result = value?.result ?? null;
      this.currentRequest.next(result ?? undefined);
      return result;
    }));
  }

  fetchRequestUser(userId?: number): Observable<UserDetail | undefined> {
    return this.http.post<ActionResult<UserDetail>>(this.environmentService.getApiUrl() + "/api/requests/userdetails", userId ?? null).pipe(
      map(value => value?.result)
    );
  }
}
